/**
 * customer-columns — the column vocabulary of the Customers list: which ids
 * exist, which are valid, what each one is called, and what a row projects into
 * a cell for it.
 *
 * The rules the drop makes structural, in one place rather than restated at
 * every call site:
 *
 *  · **Name is first and cannot move or hide.** Enforced in `normalizeColumns`
 *    (draft) and again before save, so no code path can write a layout the
 *    server would have to reject.
 *  · **Two families of id.** The 17 first-class ids are a closed list;
 *    `sis:<key>` / `custom:<key>` are PATTERN-matched, keys `[a-z0-9_]{1,64}`.
 *    Nothing here lowercases a key to make it fit: an id that does not match is
 *    invalid, and the fix is a stable mapping from CC (dep 5), not a client
 *    guess that would read a field the server never named.
 *  · **Label resolution is ordered and never leaks a key.** Translation key →
 *    authoritative catalog label → humanised field key. A missing translation
 *    renders as words, never as `customers.columns.sis:credits`.
 *  · **Null and zero are different.** A missing value is an em dash; `0` is 0.
 */
import type { CustomerDirectoryRecord, CustomerListField, CustomerListSortKey, ExternalIdSystem } from "./customers-data";

export const EM_DASH = "\u2014";

/** The 17 first-class column ids (`CUSTOMER_LIST_COLUMNS`). */
export const CUSTOMER_LIST_COLUMNS = [
  "name", "externalId", "customerType", "email", "phone", "locale", "classYear",
  "program", "campusOfRecord", "location", "appointments", "eventRegistrations",
  "eventCheckIns", "queueJoins", "lastSeenAt", "createdAt", "marketingConsent",
] as const;
export type FirstClassColumnId = (typeof CUSTOMER_LIST_COLUMNS)[number];

/** `sis:<key>` / `custom:<key>`, keys `[a-z0-9_]{1,64}`. */
export const CUSTOMER_ATTRIBUTE_COLUMN_PATTERN = /^(sis|custom):[a-z0-9_]{1,64}$/;

export const CUSTOMER_LIST_MAX_COLUMNS = 20;

/** The platform default layout — NOT an industry preset. Reset writes this. */
export const PLATFORM_DEFAULT_COLUMNS: string[] = [
  "name", "customerType", "email", "phone", "appointments", "eventRegistrations", "queueJoins", "lastSeenAt",
];

export const PLATFORM_DEFAULT_SORT: { key: CustomerListSortKey; direction: "asc" | "desc" } =
  { key: "name", direction: "asc" };

/** Server-sortable keys (`CUSTOMER_LIST_SORT_KEYS`). Only these get an
 *  affordance; nothing is sorted in the browser. */
export const CUSTOMER_LIST_SORT_KEYS: CustomerListSortKey[] = [
  "name", "customerType", "lastSeenAt", "createdAt",
  "appointments", "eventRegistrations", "eventCheckIns", "queueJoins",
];
const SORTABLE = new Set<string>(CUSTOMER_LIST_SORT_KEYS);
export const isSortableColumn = (id: string): id is CustomerListSortKey => SORTABLE.has(id);

export function isCustomerListColumnId(id: string): boolean {
  return (CUSTOMER_LIST_COLUMNS as readonly string[]).includes(id) || CUSTOMER_ATTRIBUTE_COLUMN_PATTERN.test(id);
}

export function columnSource(id: string): "standard" | "sis" | "custom" {
  if (id.startsWith("sis:")) return "sis";
  if (id.startsWith("custom:")) return "custom";
  return "standard";
}

export const attributeKey = (id: string): string | null => {
  const m = /^(?:sis|custom):(.+)$/.exec(id);
  return m ? m[1] : null;
};

/** Header translation key for a column id. Dynamic ids keep their colon — the
 *  tenant catalog supplies the override under exactly this key. */
export const customerColumnHeaderKey = (id: string): string => `customers.columns.${id}`;

/** Acronyms the sentence-caser must not lowercase into a word: `sis:gpa` read
 *  "Gpa" before this list existed. Extend it rather than special-casing at a
 *  call site — and prefer the field catalog's own label once CC dep 4 ships,
 *  since this humaniser is a last resort, not a naming authority. */
const FIELD_ACRONYMS = new Set([
  "gpa", "sis", "id", "ids", "lms", "ects", "fte", "sso", "url", "ssn", "dob", "crm", "api", "pin", "faq", "sms", "iep",
]);

/** `campus_of_record` → "Campus of record"; `advisorEmail` → "Advisor email";
 *  `gpa` → "GPA". Last resort only — a label the catalog and the catalog's
 *  translation key both failed to supply. Never the raw key, never a
 *  missing-key string. */
export function humaniseFieldKey(key: string): string {
  const words = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return key;
  const cased = words.map((w, i) => {
    if (FIELD_ACRONYMS.has(w)) return w.toUpperCase();
    return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w;
  });
  return cased.join(" ");
}

/**
 * English fallbacks for the 17 first-class ids, passed as the `t(key, fallback)`
 * second argument until `customers.columns.*` ships in the catalog (the key list
 * is in the CD reply). Without them a header renders a humanised id — readable,
 * but not the agreed wording, and "EventCheckIns" is not a column name anyone
 * chose.
 */
export const DEFAULT_COLUMN_LABELS: Record<string, string> = {
  name: "{noun}",
  externalId: "External ID",
  customerType: "Type",
  email: "Email",
  phone: "Phone",
  locale: "Language",
  classYear: "Class year",
  program: "Program",
  campusOfRecord: "Campus",
  location: "Location",
  appointments: "Appointments",
  eventRegistrations: "Registrations",
  eventCheckIns: "Check-ins",
  queueJoins: "Queue joins",
  lastSeenAt: "Last seen",
  createdAt: "Created",
  marketingConsent: "Marketing",
};

/**
 * Name is first, ids are unique, invalid ids are dropped, and the list is
 * capped. Used for the DRAFT on every edit and again immediately before save,
 * so an invalid layout cannot exist in either place.
 *
 * Retired ids are NOT dropped here: a configured field that the catalog no
 * longer offers stays in the draft, marked, and requires an explicit removal
 * (the panel's job). Silently dropping a saved selection would delete another
 * administrator's column on the next save.
 */
export function normalizeColumns(columns: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = ["name"];
  seen.add("name");
  for (const id of columns) {
    if (id === "name" || seen.has(id) || !isCustomerListColumnId(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out.slice(0, CUSTOMER_LIST_MAX_COLUMNS);
}

export const columnsEqual = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/* ── cell projection ───────────────────────────────────────────────────── */

export type CellKind = "name" | "text" | "count" | "date" | "boolean" | "type" | "identifier";

export interface CellSpec {
  kind: CellKind;
  /** Null means "no value" — the caller renders an em dash. Zero is a value. */
  value: string | number | boolean | null;
  align: "left" | "right";
}

const asScalar = (v: unknown): string | number | boolean | null => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  // A nested SIS array/object has no valid scalar projection. It is NOT
  // stringified into `[object Object]`: the honest cell is "no projected value"
  // until CC declares a typed projection for that field (dep 5).
  return null;
};

/** What a row shows in a column. Pure — formatting of dates/booleans belongs to
 *  the renderer, which owns the locale and the noun. */
export function projectCell(row: CustomerDirectoryRecord, id: string): CellSpec {
  switch (id) {
    case "name": return { kind: "name", value: row.displayName ?? null, align: "left" };
    case "externalId": return { kind: "identifier", value: row.externalId?.value ?? null, align: "left" };
    case "customerType": return { kind: "type", value: row.customerType?.id ?? row.customerTypeId ?? null, align: "left" };
    case "email": return { kind: "text", value: row.email ?? null, align: "left" };
    case "phone": return { kind: "text", value: row.phone ?? null, align: "left" };
    case "locale": return { kind: "text", value: row.locale ?? null, align: "left" };
    case "classYear": return { kind: "text", value: asScalar(row.classYear), align: "left" };
    case "program": return { kind: "text", value: row.program ?? null, align: "left" };
    case "campusOfRecord": return { kind: "text", value: row.campusOfRecord ?? null, align: "left" };
    case "location": return { kind: "text", value: row.location?.name ?? null, align: "left" };
    case "appointments": return { kind: "count", value: row.counts?.appointments ?? null, align: "right" };
    case "eventRegistrations": return { kind: "count", value: row.counts?.eventRegistrations ?? null, align: "right" };
    case "eventCheckIns": return { kind: "count", value: row.counts?.eventCheckIns ?? null, align: "right" };
    case "queueJoins": return { kind: "count", value: row.counts?.queueJoins ?? null, align: "right" };
    case "lastSeenAt": return { kind: "date", value: row.lastSeenAt ?? null, align: "left" };
    case "createdAt": return { kind: "date", value: row.createdAt ?? null, align: "left" };
    case "marketingConsent": return { kind: "boolean", value: row.marketingConsent ?? null, align: "left" };
    default: {
      const key = attributeKey(id);
      if (!key) return { kind: "text", value: null, align: "left" };
      const bag = id.startsWith("sis:") ? row.attributes?.sis : row.attributes?.custom;
      const raw = bag ? bag[key] : undefined;
      const value = asScalar(raw);
      return { kind: typeof value === "number" ? "count" : typeof value === "boolean" ? "boolean" : "text", value, align: typeof value === "number" ? "right" : "left" };
    }
  }
}

/* ── SIS profile projections (detail) ──────────────────────────────────── */

/**
 * THE SERVED SIS VIEW (JAI-1894, @v0y4ge/api-contracts `CustomerSisView`).
 *
 * Everything a person reads is resolved server-side: program titles, attribute
 * labels, the source system's display name. The projector reads ONLY the
 * served arrays/objects — `programs[]`, `attributes[]`, `holds[]`,
 * `sourceSystem`, `sync`. The pre-JAI-1894 flat keys are not read.
 *
 * Read defensively (the record arrives as `Record<string, unknown>`): a field
 * of the wrong shape is dropped, never rendered as JSON.
 */
export interface SisProgram {
  /** `programEntry.displayTitle` (+ code). Null → the program line is omitted; never an id. */
  title: string | null;
  /** Null when it would repeat the title (`titleSource === "code"`, or equal text). */
  code: string | null;
  status: string | null;
  startOn: string | null;
  expectedGraduationDate: string | null;
  /** Non-null `disciplineEntries[].displayTitle` only. */
  disciplines: string[];
}
export interface SisAttribute {
  key: string;
  /** Served `label` when mapped; the source `raw` code when not. */
  value: string;
  mapped: boolean;
}
export interface SisSync {
  lastSyncedAt: string | null;
  dataChangedAt: string | null;
  degraded: boolean;
}
/**
 * ONE HOLD, AS THE SERVER NAMES IT (JAI-1782).
 *
 * `title` and `description` are RESOLVED SERVER-SIDE from the tenant's One-Stop
 * identity-panel copy (tenant text → platform default → SIS label → category)
 * and are the only words a staff surface renders. The UI composes no label of
 * its own: the student reading the One-Stop panel and the advisor reading this
 * record must see the same wording for the same hold, and the only way to
 * guarantee that is to render what the server sent.
 *
 * `categoryRaw` (the source system's own spelling) stays for staff — they need
 * it for SIS lookups — but only ever as secondary text beside the resolved
 * title, never as the name of the hold. `category` is a slug and is NEVER shown.
 */
export interface SisHold {
  category: string;
  categoryRaw?: string;
  detail?: string;
  startOn?: string;
  endOn?: string;
  title: string;
  description: string | null;
}

export interface SisProfile {
  programs: SisProgram[];
  attributes: SisAttribute[];
  holds: SisHold[];
  sourceName: string | null;
  sync: SisSync | null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

export function projectSisProfile(attributes: Record<string, unknown> | null | undefined): SisProfile {
  const out: SisProfile = { programs: [], attributes: [], holds: [], sourceName: null, sync: null };
  if (!attributes) return out;

  if (Array.isArray(attributes.programs)) {
    out.programs = attributes.programs.filter(isRecord).map((p) => {
      const entry = isRecord(p.programEntry) ? p.programEntry : null;
      const title = entry ? str(entry.displayTitle) : null;
      const rawCode = entry ? str(entry.code) : null;
      const code = entry?.titleSource === "code" || (rawCode && title && rawCode.trim() === title.trim()) ? null : rawCode;
      return {
        title,
        code,
        status: str(p.enrollmentStatus),
        startOn: str(p.startOn),
        expectedGraduationDate: str(p.expectedGraduationDate),
        disciplines: Array.isArray(p.disciplineEntries)
          ? p.disciplineEntries.filter(isRecord).map((d) => str(d.displayTitle)).filter((x): x is string => !!x)
          : [],
      };
    });
  }

  if (Array.isArray(attributes.attributes)) {
    out.attributes = attributes.attributes.filter(isRecord).flatMap((a) => {
      const key = str(a.key);
      if (!key) return [];
      const mapped = a.mapped !== false;
      const value = mapped ? (str(a.label) ?? str(a.value)) : str(a.raw);
      return value ? [{ key, value, mapped }] : [];
    });
  }

  if (isRecord(attributes.sourceSystem)) out.sourceName = str(attributes.sourceSystem.displayName);

  if (isRecord(attributes.sync)) {
    out.sync = {
      lastSyncedAt: str(attributes.sync.lastSyncedAt),
      dataChangedAt: str(attributes.sync.dataChangedAt),
      degraded: attributes.sync.status === "degraded",
    };
  }

  {
    const value = attributes.holds;
    if (Array.isArray(value)) {
      /* A hold with no served `title` is DROPPED, not rendered from its slug.
         The resolver has a four-step fallback ending at the raw category, so a
         title-less hold means the response did not come from a server that
         resolves copy — and inventing a name here is exactly the divergence
         JAI-1782 removed. */
      out.holds = value.filter(isRecord).flatMap((h) => {
        const title = typeof h.title === "string" ? h.title : null;
        if (!title) return [];
        return [{
          category: typeof h.category === "string" ? h.category : "",
          categoryRaw: typeof h.categoryRaw === "string" ? h.categoryRaw : undefined,
          detail: typeof h.detail === "string" ? h.detail : undefined,
          startOn: typeof h.startOn === "string" ? h.startOn : undefined,
          endOn: typeof h.endOn === "string" ? h.endOn : undefined,
          title,
          description: typeof h.description === "string" ? h.description : null,
        }];
      });
    }
  }
  return out;
}

/* ── catalog helpers ───────────────────────────────────────────────────── */

/** Index a field catalog by id, keeping the FIRST entry per id — the catalog
 *  contract says each field appears once, and a duplicate must not silently
 *  double a group count. */
export function indexCatalog(fields: readonly CustomerListField[]): Map<string, CustomerListField> {
  const byId = new Map<string, CustomerListField>();
  for (const f of fields) if (!byId.has(f.id)) byId.set(f.id, f);
  return byId;
}

/** Whether the configured external-ID system makes the `externalId` column
 *  meaningful. With no system configured the column shows an em dash for every
 *  row, which is honest but useless — the panel says so in its help text. */
export const externalIdConfigured = (system: ExternalIdSystem | null | undefined): boolean => !!system;
